// Lambert belge 1972 (EPSG:31370) → GPS (WGS84, EPSG:4326), au mètre près.
//
// Deux étapes, les mêmes que PostGIS (`ST_Transform`), qui a calculé le lat/lon de toutes les adresses Belmap du
// DMP :
//   1. la projection inverse : conique conforme de Lambert à deux parallèles (méthode EPSG 9802) sur l'ellipsoïde
//      de Hayford 1924. x/y deviennent une latitude/longitude… encore dans le datum belge BD72 ;
//   2. le changement de datum BD72 → WGS84 : Helmert à 7 paramètres, convention « Position Vector » (EPSG:15929,
//      « BD72 to WGS 84 (3) », précision annoncée ~1 m), celle que porte le `+towgs84` de l'EPSG:31370.
//
// L'API a déjà un `lambert72ToWgs84` (packages/api/src/lib/geo.ts), faux de ~836 m (mesuré le 2026-08-28 contre le
// DMP) : il omet la correction de thêta (~730 m) ET le changement de datum (~108 m). Il remplit encore le lat/lon des
// adresses que l'app crée ; le remplacer par celui-ci changerait ce que l'app écrit dans le DMP, décision à part
// (2026-10-16). Celui-ci ne sert qu'à LIRE : le point montré à l'écran part des x/y, qui sont justes partout.
const DEG = Math.PI / 180;
const ARCSEC = DEG / 3600;
// Hayford 1924 (« International 1924 ») : l'ellipsoïde du BD72.
const BD72_A = 6378388;
const BD72_E2 = (1 / 297) * (2 - 1 / 297);
const BD72_E = Math.sqrt(BD72_E2);
// WGS84.
const WGS84_A = 6378137;
const WGS84_F = 1 / 298.257223563;
const WGS84_E2 = WGS84_F * (2 - WGS84_F);
// EPSG:31370 — Belge 1972 / Belgian Lambert 72. L'origine est au pôle (φF = 90°), d'où rF = 0 plus bas.
const PHI_1 = (51 + 10 / 60 + 0.00204 / 3600) * DEG;
const PHI_2 = (49 + 50 / 60 + 0.00204 / 3600) * DEG;
const LAMBDA_F = (4 + 22 / 60 + 2.952 / 3600) * DEG;
const EASTING_F = 150000.013;
const NORTHING_F = 5400088.438;
const mOf = (phi) => Math.cos(phi) / Math.sqrt(1 - BD72_E2 * Math.sin(phi) ** 2);
const tOf = (phi) => Math.tan(Math.PI / 4 - phi / 2) / ((1 - BD72_E * Math.sin(phi)) / (1 + BD72_E * Math.sin(phi))) ** (BD72_E / 2);
const N = (Math.log(mOf(PHI_1)) - Math.log(mOf(PHI_2))) / (Math.log(tOf(PHI_1)) - Math.log(tOf(PHI_2)));
const A_F = (BD72_A * mOf(PHI_1)) / (N * tOf(PHI_1) ** N);
// BD72 → WGS84 (EPSG:15929). Rotations en secondes d'arc, facteur d'échelle en ppm.
const TX = -106.8686;
const TY = 52.2978;
const TZ = -103.7239;
const RX = 0.3366 * ARCSEC;
const RY = -0.457 * ARCSEC;
const RZ = 1.8422 * ARCSEC;
const SCALE = 1 - 1.2747e-6;
/** Étape 1 seule : x/y Lambert 72 → latitude/longitude dans le datum BD72 (degrés). Exportée pour être vérifiée
 *  contre l'exemple de l'EPSG (Guidance Note 7-2) ; l'écran, lui, veut `wgs84FromLambert72`. */
export function bd72FromLambert72(x, y) {
    const dx = x - EASTING_F;
    const dy = NORTHING_F - y;
    const r = Math.hypot(dx, dy);
    const t = (r / A_F) ** (1 / N);
    let phi = Math.PI / 2 - 2 * Math.atan(t);
    for (let i = 0; i < 10; i++) {
        const eSin = BD72_E * Math.sin(phi);
        phi = Math.PI / 2 - 2 * Math.atan(t * ((1 - eSin) / (1 + eSin)) ** (BD72_E / 2));
    }
    return { lat: phi / DEG, lon: (Math.atan2(dx, dy) / N + LAMBDA_F) / DEG };
}
/** x/y Lambert 72 (mètres) → latitude/longitude WGS84 (degrés), à ~1 m près. Pur calcul, sans contrôle de
 *  domaine : un x/y absurde donne un point absurde, voir `isInBelgium`. */
export function wgs84FromLambert72(x, y) {
    const bd72 = bd72FromLambert72(x, y);
    const phi = bd72.lat * DEG;
    const lambda = bd72.lon * DEG;
    // Étape 2 : BD72 → coordonnées cartésiennes (hauteur 0 : à quelques dizaines de mètres près, l'effet sur le plan
    // est de l'ordre du millimètre), Helmert, puis retour en latitude/longitude sur l'ellipsoïde WGS84.
    const nu = BD72_A / Math.sqrt(1 - BD72_E2 * Math.sin(phi) ** 2);
    const X = nu * Math.cos(phi) * Math.cos(lambda);
    const Y = nu * Math.cos(phi) * Math.sin(lambda);
    const Z = nu * (1 - BD72_E2) * Math.sin(phi);
    const X2 = SCALE * (X - RZ * Y + RY * Z) + TX;
    const Y2 = SCALE * (RZ * X + Y - RX * Z) + TY;
    const Z2 = SCALE * (-RY * X + RX * Y + Z) + TZ;
    const p = Math.hypot(X2, Y2);
    let lat = Math.atan2(Z2, p * (1 - WGS84_E2));
    for (let i = 0; i < 10; i++) {
        const nu2 = WGS84_A / Math.sqrt(1 - WGS84_E2 * Math.sin(lat) ** 2);
        lat = Math.atan2(Z2 + WGS84_E2 * nu2 * Math.sin(lat), p);
    }
    return { lat: lat / DEG, lon: Math.atan2(Y2, X2) / DEG };
}
/** La Belgique et ~5 km de marge (extrêmes : Torgny 49,50° N, Meerle 51,50° N, La Panne 2,55° E, Ouren 6,41° E) :
 *  de quoi écarter un 0/0 — qui tombe vers Beauvais — ou un x/y saisi de travers, sans rogner les communes
 *  frontalières. */
export function isInBelgium(p) {
    return Number.isFinite(p.lat) && Number.isFinite(p.lon)
        && p.lat >= 49.45 && p.lat <= 51.55 && p.lon >= 2.5 && p.lon <= 6.45;
}
//# sourceMappingURL=lambert72.js.map